import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import { useContext, useState } from 'react';

import { route } from './route';
import { ApplicationContext } from '../../../../shared/source/context/context';
import { useConsentMutation } from '../../../../shared/source/query-hooks/consent';
import { useTermsQuery } from '../../../../shared/source/query-hooks/terms';
import { useDeleteProfile } from '../../../../shared/source/query-hooks/use-delete-profile';
import { onEnterKeyDownPreventDefault } from '../common/form';
import { useSnackbars } from '../error-handling/SnackBars';

import type { Consent } from '../../../../shared/source/data-contract/terms';
import type { FormikHelpers } from 'formik';
import type { FunctionComponent } from 'react';

const consentDialogId = 'data-protection-consent-dialog-title';
export const NoConsentDialog: FunctionComponent = () => {
    const { actions, translate } = useContext(ApplicationContext);
    const { mutate } = useDeleteProfile(() => {
        actions.logout();
    });
    const { showErrorSnackbar } = useSnackbars();

    const goBack = (): void => actions.navigateTo({}, { replace: true });
    const deleteAccount = (): void => {
        mutate(undefined, {
            onError: (error) => {
                showErrorSnackbar(error);
                goBack();
            },
        });
    };

    return (
        <Dialog open={true}>
            <DialogTitle id={consentDialogId} title={translate('dialogs.no-consent.title')} />
            <DialogContent>
                <Typography variant="body1">{translate('dialogs.no-consent.content')}</Typography>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={deleteAccount}>
                    {translate('dialogs.no-consent.delete')}
                </Button>
                <Button type="submit" onClick={goBack}>
                    {translate('dialogs.no-consent.back')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
const ProceedWithConsentButton: FunctionComponent = () => {
    const formik = useFormikContext<ConsentFormState>();
    const { translate } = useContext(ApplicationContext);

    return (
        <Button disabled={formik.isSubmitting || !formik.values.consent} type="submit">
            {translate('dialogs.terms.proceed')}
        </Button>
    );
};

type TermsContentProps = {
    readonly shortText: string;
    readonly text: string;
};
const StyledConditions = styled('section')({
    fontSize: '0.875rem', // 14px on 16px browser default
    lineHeight: '2em',
});
const TermsContent: FunctionComponent<TermsContentProps> = (props) => {
    const { translate } = useContext(ApplicationContext);
    const [showShortTerms, setShowShortTerms] = useState(true);

    if (showShortTerms) {
        return (
            <article>
                <StyledConditions dangerouslySetInnerHTML={{ __html: props.shortText }} />
                <Button
                    sx={{
                        my: 1,
                        pl: 0,
                    }}
                    type="button"
                    variant="text"
                    onClick={() => setShowShortTerms(false)}
                >
                    {translate('common.read-more')}
                </Button>
            </article>
        );
    }

    return <StyledConditions dangerouslySetInnerHTML={{ __html: props.text }} />;
};
const validate =
    (message: string) =>
    ({ consent }: ConsentFormState) =>
        consent ? {} : { consent: message };

type ConsentFormState = {
    readonly consent: boolean;
};
const useDeclineConsent = (): (() => void) => {
    const { actions } = useContext(ApplicationContext);
    const search = new URLSearchParams({ [route.searchParams.decline]: 'true' }).toString();

    return () => actions.navigateTo({ search }, { replace: true });
};

type TermsFormProps = {
    readonly consentStatus: Consent['status'];
};

export const TermsForm: FunctionComponent<TermsFormProps> = ({ consentStatus }) => {
    const { actions, applicationState, translate } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const { reloadTerms, termsQuery } = useTermsQuery();
    const consentMutation = useConsentMutation();

    const declineConsent = useDeclineConsent();

    const onCancel = myUserProfile.get('exists').mapOr(declineConsent, (exists) => {
        if (exists === false) {
            return actions.logout;
        }

        return declineConsent;
    });

    if (termsQuery.isError) {
        return <DialogContent>{translate('dialogs.terms.load-error')}</DialogContent>;
    }

    if (termsQuery.isPending) {
        return <DialogContent>{translate('dialogs.terms.loading')}</DialogContent>;
    }

    const terms = termsQuery.data;
    const latestUpdate = new Date(terms.validFrom).toLocaleDateString(applicationState.languageKey);
    const updateNotice =
        consentStatus === 'outdated' ? (
            <DialogContent>{translate('dialogs.terms.last-updated', { date: latestUpdate })}</DialogContent>
        ) : null;

    const onSubmit = (
        { consent }: { readonly consent: boolean },
        formik: FormikHelpers<{ readonly consent: boolean }>,
    ): void =>
        consentMutation.mutate(
            {
                id: terms.id,
                accepted: consent,
                language: terms.language,
            },
            {
                onError: () =>
                    // Edge case: Consent fails because terms id is outdated
                    reloadTerms().then(() => {
                        formik.resetForm();
                        formik.setSubmitting(false);
                    }),
            },
        );

    const consentMessage = translate('dialogs.terms.validation-message');

    return (
        <Formik
            initialValues={{ consent: false }}
            validate={validate(consentMessage)}
            validateOnChange={true}
            onSubmit={onSubmit}
        >
            <Form onKeyDown={onEnterKeyDownPreventDefault}>
                <DialogContent>
                    {updateNotice}
                    <TermsContent shortText={terms.shortText} text={terms.text} />
                    <Box alignItems="center" component="label" display="flex" htmlFor="consent" mt={2}>
                        <Field id="consent" name="consent" type="checkbox" />
                        <StyledConditions
                            sx={{
                                cursor: 'pointer',
                                display: 'inline-block',
                                pl: 1,
                            }}
                        >
                            {translate('dialogs.terms.consent-label')}
                        </StyledConditions>
                    </Box>
                    <ErrorMessage name="consent">
                        {(msg) => (
                            <StyledConditions>
                                <Typography color="error" variant="body2">
                                    {msg}
                                </Typography>
                            </StyledConditions>
                        )}
                    </ErrorMessage>
                </DialogContent>
                <DialogActions>
                    <Button type="button" onClick={onCancel}>
                        {translate('dialogs.buttons.cancel')}
                    </Button>
                    <ProceedWithConsentButton />
                </DialogActions>
            </Form>
        </Formik>
    );
};
